import { discoverCategoryId } from "./discoverContent";
import { discoverClient } from "../discoverClient";

/**
 * Discover feedback that does not need an account in v1.
 *
 * Signed-in people get the server copy (so hidden apps stay hidden across
 * devices); everyone else keeps the same choices in this browser. The two
 * stores share the shape the UI needs, not a server identity.
 */
export type DiscoverFeedback = "helpful" | "not_interested" | "hidden";

export const DISCOVER_HIDDEN_KEY = "ato.discover.hidden.v1";
export const DISCOVER_RATINGS_KEY = "ato.discover.ratings.v1";
/** Anonymous hidden apps have no server title; remember the one we showed. */
export const DISCOVER_HIDDEN_TITLES_KEY = "ato.discover.hidden.titles.v1";
export const DISCOVER_PERSONALIZATION_KEY = "ato.discover.personalization.v1";
export const DISCOVER_AFFINITY_KEY = "ato.discover.affinity.v1";

export type LocalRating = Exclude<DiscoverFeedback, "hidden">;
export type HiddenAppSummary = { id: string; title: string };

function readJson<T>(key: string, fallback: T): T {
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? "null") as T | null;
    return value ?? fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Storage can be unavailable; the visible session still works. */
  }
}

function readStringArray(key: string): string[] {
  const value = readJson<unknown>(key, []);
  return Array.isArray(value)
    ? value.filter((id): id is string => typeof id === "string" && !!id)
    : [];
}

export function readHiddenIds(): string[] {
  return readStringArray(DISCOVER_HIDDEN_KEY);
}

function writeHiddenIds(ids: readonly string[]): void {
  writeJson(DISCOVER_HIDDEN_KEY, [...new Set(ids)]);
}

export function isHiddenLocally(appId: string): boolean {
  return readHiddenIds().includes(appId);
}

export function readRatings(): Record<string, LocalRating> {
  const value = readJson<unknown>(DISCOVER_RATINGS_KEY, {});
  if (value === null || typeof value !== "object" || Array.isArray(value))
    return {};
  const ratings: Record<string, LocalRating> = {};
  for (const [id, rating] of Object.entries(value as Record<string, unknown>)) {
    if (
      typeof id === "string" &&
      (rating === "helpful" || rating === "not_interested")
    )
      ratings[id] = rating;
  }
  return ratings;
}

export function localRating(appId: string): LocalRating | null {
  return readRatings()[appId] ?? null;
}

/** The anonymous list remembers the title it showed, so Settings can name it. */
export function rememberHiddenTitle(appId: string, title: string): void {
  if (!title) return;
  const titles = readJson<Record<string, string>>(
    DISCOVER_HIDDEN_TITLES_KEY,
    {},
  );
  const next: Record<string, string> = {};
  for (const [id, value] of Object.entries(titles))
    if (typeof value === "string") next[id] = value;
  next[appId] = title;
  writeJson(DISCOVER_HIDDEN_TITLES_KEY, next);
}

export function rememberedHiddenTitle(appId: string): string | null {
  const title = readJson<Record<string, unknown>>(
    DISCOVER_HIDDEN_TITLES_KEY,
    {},
  )[appId];
  return typeof title === "string" && title ? title : null;
}

function hideLocally(appId: string, title: string): void {
  rememberHiddenTitle(appId, title);
  writeHiddenIds([...readHiddenIds(), appId]);
}

function unhideLocally(appId: string): void {
  writeHiddenIds(readHiddenIds().filter((id) => id !== appId));
  const titles = readJson<Record<string, unknown>>(
    DISCOVER_HIDDEN_TITLES_KEY,
    {},
  );
  delete titles[appId];
  writeJson(DISCOVER_HIDDEN_TITLES_KEY, titles);
}

function rateLocally(appId: string, rating: LocalRating): void {
  writeJson(DISCOVER_RATINGS_KEY, { ...readRatings(), [appId]: rating });
}

function clearLocalRating(appId: string): void {
  const ratings = readRatings();
  delete ratings[appId];
  writeJson(DISCOVER_RATINGS_KEY, ratings);
}

/** Personalization and measurement default on; only the literal "off" disables. */
export function isDiscoverPersonalizationEnabled(): boolean {
  try {
    return localStorage.getItem(DISCOVER_PERSONALIZATION_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setDiscoverPersonalizationEnabled(enabled: boolean): void {
  try {
    if (enabled) localStorage.removeItem(DISCOVER_PERSONALIZATION_KEY);
    else localStorage.setItem(DISCOVER_PERSONALIZATION_KEY, "off");
  } catch {
    /* Storage can be unavailable; the choice lasts for this session. */
  }
  // OFF deletes the local profile, so turning it back on starts from nothing
  // rather than from interests the person asked us to forget.
  if (!enabled) {
    try {
      localStorage.removeItem(DISCOVER_AFFINITY_KEY);
    } catch {
      /* Nothing else to clear. */
    }
  }
}

export type DiscoverFeedbackInput = {
  appId: string;
  title: string;
  action: DiscoverFeedback;
  signedIn: boolean;
};

/**
 * Apply one menu choice. The row is already gone from the rendered list; this
 * only persists it. A signed-in write that fails keeps the local copy so the
 * person's choice is not silently undone — Settings then shows the local list.
 */
export async function submitDiscoverFeedback(
  input: DiscoverFeedbackInput,
): Promise<void> {
  if (!input.signedIn) {
    persistLocally(input);
    return;
  }
  try {
    if (input.action === "hidden") await discoverClient.hideApp(input.appId);
    else await discoverClient.rateApp(input.appId, input.action);
  } catch {
    persistLocally(input);
  }
}

function persistLocally(input: DiscoverFeedbackInput): void {
  if (input.action === "hidden") hideLocally(input.appId, input.title);
  else rateLocally(input.appId, input.action);
}

/** Undo is the inverse of the stored choice, never a guess from current state. */
export async function undoDiscoverFeedback(input: {
  appId: string;
  action: DiscoverFeedback;
  signedIn: boolean;
}): Promise<void> {
  if (input.action === "hidden") {
    if (input.signedIn) {
      try {
        await discoverClient.unhideApp(input.appId);
      } catch {
        /* Restoring the row must not depend on the network. */
      }
    }
    unhideLocally(input.appId);
    return;
  }
  if (input.signedIn) {
    try {
      await discoverClient.clearRating(input.appId);
    } catch {
      /* Same: the row comes back either way. */
    }
  }
  clearLocalRating(input.appId);
}

/**
 * The Settings "Hidden apps" list. Signed-in reads the account copy; otherwise
 * the local ids are named by the titles remembered at hide time.
 */
export async function loadHiddenApps(
  signedIn: boolean,
): Promise<HiddenAppSummary[]> {
  if (!signedIn) {
    return readHiddenIds().map((id) => ({
      id,
      title: rememberedHiddenTitle(id) ?? id,
    }));
  }
  const { apps } = await discoverClient.hiddenApps();
  return apps
    .filter(
      (app): app is HiddenAppSummary =>
        !!app &&
        typeof app.id === "string" &&
        typeof app.title === "string",
    )
    .map((app) => ({ id: app.id, title: app.title }));
}

/** Settings "Show again": the server copy first, then the local one. */
export async function restoreHiddenApp(
  appId: string,
  signedIn: boolean,
): Promise<void> {
  if (signedIn) {
    try {
      await discoverClient.unhideApp(appId);
    } catch {
      /* The local list still forgets it; a later read reconciles. */
    }
  }
  unhideLocally(appId);
}

/** How often each category appeared in a detail view or a helpful rating. */
export function readAffinity(): Record<string, number> {
  const value = readJson<unknown>(DISCOVER_AFFINITY_KEY, {});
  if (value === null || typeof value !== "object" || Array.isArray(value))
    return {};
  const profile: Record<string, number> = {};
  for (const [category, count] of Object.entries(value as Record<string, unknown>))
    if (
      typeof category === "string" &&
      category &&
      typeof count === "number" &&
      Number.isFinite(count) &&
      count > 0
    )
      profile[category] = count;
  return profile;
}

/**
 * Interest is only remembered while personalization is on; turning it off
 * deletes the profile (see setDiscoverPersonalizationEnabled).
 */
export function bumpDiscoverAffinity(
  category: string | null | undefined,
): void {
  if (!category || !isDiscoverPersonalizationEnabled()) return;
  const profile = readAffinity();
  profile[category] = (profile[category] ?? 0) + 1;
  writeJson(DISCOVER_AFFINITY_KEY, profile);
}

/** The category For You asks the server for; null means plain Recommended. */
export function topAffinityCategory(): string | null {
  if (!isDiscoverPersonalizationEnabled()) return null;
  // Interest recorded before genres counts toward the genre it became.
  const counts: Record<string, number> = {};
  for (const [stored, count] of Object.entries(readAffinity())) {
    const category = discoverCategoryId(stored);
    if (category && category !== "bugs") counts[category] = (counts[category] ?? 0) + count;
  }
  let best: string | null = null;
  let bestCount = 0;
  for (const [category, count] of Object.entries(counts))
    if (count > bestCount) {
      best = category;
      bestCount = count;
    }
  return best;
}
